<template>
  <div class="user-message">
    <div
      v-if="message.referenceFileUrlList?.length"
      ref="uploadedFileHistoryRef"
      class="uploaded-file-history"
      :class="{ 'uploaded-file-history--collapsed': !fileInfoExpand }"
      :style="{ width: uploadedFileHistoryWidth }"
    >
      <UploadedFileCard
        v-for="(file, index) in message.referenceFileUrlList"
        :key="file.attachIndex || file.id || index"
        :file="file"
      />
      <div v-if="shouldShowUploadedFileViewAll" class="uploaded-file-history__action">
        <button class="uploaded-file-history__view-all" type="button" @click="handleViewAllFiles">
          查看全部
        </button>
      </div>
    </div>

    <div v-if="message.content" class="user-message__bubble">
      <span class="user-message__text" v-html="displayContent.html"></span>
    </div>

    <div class="user-message__meta">
      <time v-if="sendTime" class="user-message__time" :datetime="messageItem.processedAt">
        {{ sendTime }}
      </time>
      <button class="user-message__copy" type="button" @click="handleCopy">
        <i class="icon-file-copy-line t-iconfont"></i>
        <span>复制</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import UploadedFileCard from '@/components/upload/UploadedFileCard.vue'
import { formatMessageDisplayTime } from '../use-qoder-message-format'

defineOptions({
  name: 'UserMessage'
})

const props = defineProps({
  messageItem: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['handle-copy'])

const fileInfoExpand = ref(false)
const uploadedFileHistoryRef = ref(null)
// 折叠状态下，文件列表的实际内容是否超过三行。
const uploadedFileOverflow = ref(false)
// 根据当前一行可容纳的卡片数量计算出的文件列表宽度。
const uploadedFileHistoryWidth = ref('')
let uploadedFileResizeObserver = null
// 单张卡片宽 200px，加上左侧 8px 间距后，每张卡片实际占用 208px。
const UPLOADED_FILE_CARD_WIDTH = 208
// 文件列表设计稿最大宽度，最多可完整容纳三张卡片。
const UPLOADED_FILE_HISTORY_MAX_WIDTH = 630

const message = computed(() => props.messageItem.message || {})
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (char) => {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
    return entities[char]
  })
const displayContent = computed(() => {
  const content = message.value.content || ''
  const skillPattern = /\{"skillName":"(?:\\.|[^"\\])*","skillId":"(?:\\.|[^"\\])*"\}/g
  let html = ''
  let text = ''
  let cursor = 0

  for (const match of content.matchAll(skillPattern)) {
    const before = content.slice(cursor, match.index)
    html += escapeHtml(before)
    text += before

    try {
      const skill = JSON.parse(match[0])
      if (skill.skillName && skill.skillId) {
        html += `<span class="user-message__skill">${escapeHtml(skill.skillName)}</span>`
        text += skill.skillName
      } else {
        html += escapeHtml(match[0])
        text += match[0]
      }
    } catch {
      html += escapeHtml(match[0])
      text += match[0]
    }
    cursor = match.index + match[0].length
  }

  const after = content.slice(cursor)
  return { html: html + escapeHtml(after), text: text + after }
})
const sendTime = computed(() => formatMessageDisplayTime(props.messageItem.processedAt))

const shouldShowUploadedFileViewAll = computed(
  () => uploadedFileOverflow.value && !fileInfoExpand.value
)

// 折叠状态下通过实际渲染高度判断内容是否超过三行。
const updateUploadedFileOverflow = () => {
  const element = uploadedFileHistoryRef.value
  uploadedFileOverflow.value = Boolean(
    // scrollHeight 是全部卡片高度，clientHeight 是折叠后的三行高度。
    element && !fileInfoExpand.value && element.scrollHeight > element.clientHeight
  )
}

// 根据父容器的可用宽度计算当前列数，并将列表宽度收敛到整列卡片宽度。
const updateUploadedFileLayout = () => {
  const element = uploadedFileHistoryRef.value
  if (!element) return

  // 可用宽度不能超过文件列表的设计稿最大宽度。
  const availableWidth = Math.min(
    element.parentElement?.clientWidth || UPLOADED_FILE_HISTORY_MAX_WIDTH,
    UPLOADED_FILE_HISTORY_MAX_WIDTH
  )
  const fileCount = message.value.referenceFileUrlList?.length || 0
  // 用可用宽度除以单张卡片占用宽度得到列数，同时不超过实际文件数量且至少保留一列。
  const columnCount = Math.max(
    1,
    Math.min(fileCount, Math.floor(availableWidth / UPLOADED_FILE_CARD_WIDTH))
  )
  // 精确的整列宽度让“查看全部”遮罩与一整行卡片保持同宽。
  uploadedFileHistoryWidth.value = `${columnCount * UPLOADED_FILE_CARD_WIDTH}px`
  // 等列表宽度更新并重新换行后，再判断实际内容是否超过三行。
  nextTick(updateUploadedFileOverflow)
}

onMounted(() => {
  // 同时监听文件列表和父容器，保证列表自身或页面宽度变化后都重新计算。
  uploadedFileResizeObserver = new ResizeObserver(updateUploadedFileLayout)
  if (uploadedFileHistoryRef.value) {
    uploadedFileResizeObserver.observe(uploadedFileHistoryRef.value)
    if (uploadedFileHistoryRef.value.parentElement) {
      uploadedFileResizeObserver.observe(uploadedFileHistoryRef.value.parentElement)
    }
  }
  nextTick(updateUploadedFileLayout)
})

watch(
  () => message.value.referenceFileUrlList,
  // 文件新增、删除或更新后，等待 DOM 渲染完成再重新计算列数和溢出状态。
  () => nextTick(updateUploadedFileLayout),
  { deep: true }
)

// v-if 导致文件列表元素切换时，及时更新 ResizeObserver 监听的元素。
watch(uploadedFileHistoryRef, (element, previousElement) => {
  if (previousElement) {
    uploadedFileResizeObserver?.unobserve(previousElement)
    if (previousElement.parentElement) {
      uploadedFileResizeObserver?.unobserve(previousElement.parentElement)
    }
  }
  if (element) {
    uploadedFileResizeObserver?.observe(element)
    if (element.parentElement) uploadedFileResizeObserver?.observe(element.parentElement)
  }
  nextTick(updateUploadedFileLayout)
})

onBeforeUnmount(() => {
  uploadedFileResizeObserver?.disconnect()
})

const handleViewAllFiles = () => {
  fileInfoExpand.value = true
}

const handleCopy = () => {
  emit('handle-copy', displayContent.value.text.trim())
}
</script>

<style lang="scss" scoped>
.user-message {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 16px;

  &__bubble {
    max-width: 630px;
    padding: 8px 16px;
    border-radius: 16px;
    background: #f2f3f5;
    color: #323233;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    word-break: break-word;
  }

  &__text {
    flex: 1;
    min-width: 0;
    white-space: pre-wrap;
    line-height: 24px;
    font-size: 15px;
  }

  :deep(.user-message__skill) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0 4px;
    padding: 2px 8px;
    border-radius: 8px;
    background: #f2edff;
    color: #6543e5;
    font-size: 12px;
    line-height: 18px;
    vertical-align: middle;

    &::before {
      content: '\e9c7';
      font-family: 't-iconfont';
      font-size: 14px;
      line-height: 14px;
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }

  &__time {
    margin-right: 8px;
    color: #858e9f;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
  }

  &__copy {
    display: flex;
    align-items: center;
    height: 24px;
    padding: 4px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #969799;
    cursor: pointer;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;

    &:hover {
      color: #323233;
      background: #f2f3f5;
    }

    .t-iconfont {
      flex: none;
      margin-right: 8px;
      font-size: 16px;
      line-height: 16px;
    }
  }
}

.uploaded-file-history {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: fit-content;
  max-width: 630px;
  margin-bottom: 8px;
  position: relative;

  &--collapsed {
    // 单行高度为卡片 54px 加下间距 8px，186px 刚好展示三行。
    max-height: 186px;
    overflow: hidden;
  }

  :deep(.uploaded-file-card) {
    margin: 0 0 8px 8px;
  }

  &__action {
    position: absolute;
    width: 100%;
    bottom: 0;
    border-bottom-right-radius: 16px;
    border-bottom-left-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 16px 12px;
    box-sizing: border-box;
    background: linear-gradient(180deg, #ffffff00 0%, #ffffff 80%);
  }

  &__view-all {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-width: 64px;
    height: 24px;
    padding: 4px 8px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #ffffff;
    color: #323233;
    cursor: pointer;
    font-size: 12px;
    line-height: 16px;
  }
}
</style>
